import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ComplianceSemaphore from "../components/ComplianceSemaphore";
import IAExtractionPanel from "../components/IAExtractionPanel";
import { judicialService, aiService, api } from "@/services/api";
import { mapDbJudicialToFrontend } from "@/services/mapping";
import type { JudicialRequirement } from "@/mocks/judicialRequirements";

export default function JudicialRequerimientoDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [requirement, setRequirement] = useState<any | null>(null);
  const [extraction, setExtraction] = useState<any>(null);
  const [tasks, setTasks] = useState<any[]>([]);
  const [logs, setLogs] = useState<any[]>([]);
  const [inputs, setInputs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [processing, setProcessing] = useState(false);

  const handleDownloadFile = (fileName: string, fileContent: string = "Simulated document content.") => {
    const element = document.createElement("a");
    const file = new Blob([fileContent], { type: 'text/plain;charset=utf-8' });
    element.href = URL.createObjectURL(file);
    element.download = fileName;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleAction = async () => {
    if (!requirement) return;
    try {
      setProcessing(true);
      if (requirement.estado === "borrador_generado" || requirement.estado === "en_revision" || requirement.estado === "en_proceso") {
        await judicialService.updateStatus(requirement.id, "respondido");
        
        setRequirement((prev: any) => prev ? { ...prev, estado: "respondido", fechaEnvio: new Date().toISOString().split("T")[0], comprobanteEnvioNombre: `Comprobante_Envio_${prev.rit}.pdf` } : null);
        setLogs((prev) => [
          {
            id: Date.now().toString(),
            accion: "Enviado a tribunal",
            detalle: "El requerimiento judicial ha sido respondido y enviado al tribunal correspondiente.",
            userName: "Usuario Actual",
            createdAt: new Date().toISOString()
          },
          ...prev
        ]);
        alert("Requerimiento enviado a tribunal exitosamente.");
      } else {
        await judicialService.updateStatus(requirement.id, "en_proceso");
        
        setRequirement((prev: any) => prev ? { ...prev, estado: "borrador_generado" } : null);
        
        setExtraction({
          id: Date.now().toString(),
          judicialRequirementId: requirement.id,
          extractedJson: {
            tribunal: requirement.tribunal || "1° Juzgado de Familia de Santiago",
            rit: requirement.rit || "C-123-2026",
            ruc: requirement.ruc || "2600123456-7",
            numeroCausa: "1",
            estudiante: requirement.studentName || "Juanito Pérez",
            curso: requirement.curso || "7° Básico",
            fechaRecepcion: requirement.fechaRecepcion || "2026-06-10",
            fechaLimite: requirement.fechaLimite || "2026-06-30",
            nivelUrgencia: requirement.prioridad === "critica" ? "Crítica" : "Alta",
            informesSolicitados: ["Informe de Convivencia", "Informe Psicológico"],
            antecedentesRequeridos: ["Registro de asistencia", "Hoja de vida"],
            accionesRecomendadas: ["Solicitar insumos a docentes", "Coordinar entrevista"],
            advertencias: []
          },
          validatedBy: "u4",
          validatedByName: "Valentina Soto",
          validatedAt: new Date().toISOString()
        });

        setLogs((prev) => [
          {
            id: Date.now().toString(),
            accion: "Informe IA Generado",
            detalle: "Se ha analizado el documento del tribunal y generado el borrador de informe.",
            userName: "Sistema IA",
            createdAt: new Date().toISOString()
          },
          ...prev
        ]);
        alert("Informe IA generado y datos extraídos correctamente.");
      }
    } catch (err: any) {
      console.error("Error executing action:", err);
      alert("Error al actualizar estado del requerimiento: " + (err.response?.data?.detail || err.message));
    } finally {
      setProcessing(false);
    }
  };

  useEffect(() => {
    async function loadData() {
      if (!id) return;
      try {
        setLoading(true);
        setError("");
        const data = await judicialService.get(id);
        if (data && data.id) {
          const req = mapDbJudicialToFrontend(data);
          
          // Map backend states to frontend expected states
          if (data.estado === "pendiente") req.estado = "recibido";
          else if (data.estado === "en_proceso") req.estado = "borrador_generado";
          else if (data.estado === "respondido") {
            req.estado = "enviado_tribunal";
            req.fechaEnvio = data.fecha_respuesta || new Date().toISOString().split("T")[0];
            req.comprobanteEnvioNombre = `Comprobante_Envio_${req.rit}.pdf`;
          }

          setRequirement(req);
          
          // Load related inputs
          let relatedInputs: any[] = [];
          if (data.caso_id) {
            const allInsumos = await api.get("/insumos/solicitados").then(res => res.data).catch(() => []);
            if (Array.isArray(allInsumos)) {
              relatedInputs = allInsumos.filter((ins) => String(ins.caso_id) === String(data.caso_id)).map((ins) => ({
                id: ins.id,
                tipoInsumo: ins.tipo_insumo,
                descripcion: ins.instrucciones || "Solicitud de antecedentes del estudiante",
                responsableName: ins.docente_nombre || "Docente",
                responsableRol: "docente",
                fechaLimite: ins.plazo_entrega ? new Date(ins.plazo_entrega).toISOString().split("T")[0] : "",
                estado: ins.estado
              }));
            }
          }
          setInputs(relatedInputs);

          // Populate standard extraction
          setExtraction({
            id: "e1",
            judicialRequirementId: req.id,
            extractedJson: {
              tribunal: req.tribunal,
              rit: req.rit,
              ruc: req.ruc || "No especificado",
              numeroCausa: "1",
              estudiante: req.studentName,
              curso: data.estudiante_curso || "Sin curso",
              fechaRecepcion: req.fechaRecepcion,
              fechaLimite: req.fechaLimite,
              nivelUrgencia: req.prioridad === "critica" ? "Crítica" : req.prioridad === "alta" ? "Alta" : "Media",
              informesSolicitados: [req.tipoRequerimiento || "Informe integral"],
              antecedentesRequeridos: ["Registro de asistencia", "Hoja de vida"],
              accionesRecomendadas: ["Solicitar insumos a docentes", "Coordinar entrevista"],
              advertencias: []
            },
            validatedBy: "u4",
            validatedByName: "Valentina Soto",
            validatedAt: req.createdAt
          });

          // Generate static tasks for compliance view
          setTasks([
            {
              id: "t1",
              titulo: "Recopilar asistencia y hoja de vida",
              descripcion: "Obtener registros oficiales del inspectoría",
              responsableName: "Patricio Moraga",
              fechaLimite: req.fechaLimite,
              estado: req.estado === "enviado_tribunal" ? "completada" : "pendiente"
            },
            {
              id: "t2",
              titulo: "Redactar informe consolidador",
              descripcion: "Consolidar información social y pedagógica",
              responsableName: "Valentina Soto",
              fechaLimite: req.fechaLimite,
              estado: req.estado === "enviado_tribunal" ? "completada" : "pendiente"
            }
          ]);

          // Trazabilidad logs
          const logsList = [
            {
              id: "l1",
              accion: "Requerimiento registrado",
              detalle: "El requerimiento judicial ha sido ingresado al sistema.",
              userName: "Valentina Soto",
              createdAt: req.createdAt || new Date().toISOString()
            }
          ];
          if (data.estado === "respondido") {
            logsList.unshift({
              id: "l2",
              accion: "Respondido",
              detalle: "Se ha remitido la respuesta al tribunal.",
              userName: "Valentina Soto",
              createdAt: data.fecha_respuesta || new Date().toISOString()
            });
          }
          setLogs(logsList);
        }
      } catch (err: any) {
        console.error("Error loading requirement details:", err);
        setError("Error al cargar detalles: " + (err.response?.data?.detail || err.message));
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <span className="flex items-center justify-center w-8 h-8">
          <i className="ri-loader-4-line text-2xl text-foreground-300 animate-spin" />
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-red-50 flex items-center justify-center">
          <i className="ri-error-warning-line text-2xl text-red-500" />
        </div>
        <p className="text-foreground-500">{error}</p>
        <button onClick={() => navigate("/judicial")} className="mt-4 text-sm text-primary-500 hover:text-primary-600 cursor-pointer font-medium">Volver a Requerimientos</button>
      </div>
    );
  }

  if (!requirement) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
          <i className="ri-error-warning-line text-2xl text-foreground-300" />
        </div>
        <p className="text-foreground-500">Requerimiento no encontrado</p>
        <button onClick={() => navigate("/judicial")} className="mt-4 text-sm text-primary-500 hover:text-primary-600 cursor-pointer font-medium">Volver a Requerimientos</button>
      </div>
    );
  }

  const hoy = new Date();
  const limite = new Date(requirement.fechaLimite);
  const diasRestantes = Math.ceil((limite.getTime() - hoy.getTime()) / (1000 * 60 * 60 * 24));

  const getPrioridadColor = (prio: string) => {
    if (prio === "critica") return "bg-red-50 text-red-700 border-red-200";
    if (prio === "alta") return "bg-orange-50 text-orange-700 border-orange-200";
    if (prio === "media") return "bg-yellow-50 text-yellow-700 border-yellow-200";
    return "bg-background-50 text-foreground-600 border-background-200";
  };

  const getJudicialStateColor = (state: string) => {
    if (state === "recibido") return "bg-blue-50 text-blue-700 border-blue-200";
    if (state === "borrador_generado") return "bg-amber-50 text-amber-700 border-amber-200";
    if (state === "enviado_tribunal" || state === "respondido") return "bg-green-50 text-green-700 border-green-200";
    return "bg-background-50 text-foreground-600 border-background-200";
  };

  const getJudicialStateLabel = (state: string) => {
    if (state === "recibido") return "Recibido";
    if (state === "borrador_generado") return "Borrador Generado";
    if (state === "enviado_tribunal" || state === "respondido") return "Enviado a Tribunal";
    return state;
  };

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Back + Header */}
      <div>
        <button onClick={() => navigate("/judicial")} className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer mb-3">
          <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line" /></span>
          Volver a Requerimientos
        </button>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm text-foreground-500 font-mono">RIT {requirement.rit}</span>
              <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium border ${getPrioridadColor(requirement.prioridad)}`}>
                {requirement.prioridad === "critica" ? "Crítica" : requirement.prioridad === "alta" ? "Alta" : requirement.prioridad === "media" ? "Media" : "Baja"}
              </span>
              <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium border ${getJudicialStateColor(requirement.estado)}`}>
                {getJudicialStateLabel(requirement.estado)}
              </span>
            </div>
            <h1 className="text-xl md:text-2xl font-bold text-foreground-900 tracking-tight">{requirement.tribunal}</h1>
            <p className="text-sm text-foreground-500 mt-1">{requirement.tipoRequerimiento}</p>
          </div>

          <div className="flex items-center gap-2">
            {requirement.estado === "enviado_tribunal" || requirement.estado === "respondido" ? null : (
              <button
                onClick={handleAction}
                disabled={processing}
                className="flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-white rounded-xl text-sm font-semibold hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap shadow-soft disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  {processing ? (
                    <i className="ri-loader-4-line animate-spin" />
                  ) : (
                    <i className="ri-file-text-line" />
                  )}
                </span>
                {processing
                  ? "Procesando..."
                  : requirement.estado === "borrador_generado" || requirement.estado === "en_revision" || requirement.estado === "en_proceso"
                  ? "Enviar a tribunal"
                  : "Generar informe IA"}
              </button>
            )}
            <button className="flex items-center gap-2 px-4 py-2.5 border border-background-200/70 rounded-xl text-sm font-medium text-foreground-600 hover:bg-background-50 transition-all duration-200 cursor-pointer whitespace-nowrap">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-more-2-fill" /></span>
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Info Grid */}
          <div className="bg-white border border-background-200/70 rounded-xl p-5">
            <h3 className="text-sm font-bold text-foreground-900 mb-4">Información del Requerimiento</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <InfoField label="Tribunal" value={requirement.tribunal} icon="ri-building-line" />
              <InfoField label="RIT" value={requirement.rit} icon="ri-scales-3-line" />
              <InfoField label="RUC" value={requirement.ruc} icon="ri-file-code-line" />
              <InfoField label="N° Causa" value={requirement.numeroCausa} icon="ri-hashtag" />
              <InfoField label="Estudiante" value={requirement.studentName || "No especificado"} icon="ri-user-line" />
              <InfoField label="Recibido" value={requirement.fechaRecepcion ? new Date(requirement.fechaRecepcion).toLocaleDateString("es-CL", { day: "2-digit", month: "2-digit", year: "numeric" }) : ""} icon="ri-calendar-line" />
              <InfoField
                label="Fecha límite"
                value={requirement.fechaLimite ? new Date(requirement.fechaLimite).toLocaleDateString("es-CL", { day: "2-digit", month: "2-digit", year: "numeric" }) : ""}
                icon="ri-alarm-line"
                highlight={diasRestantes <= 3}
              />
              <InfoField label="Responsable" value={requirement.responsableName || "Sin asignar"} icon="ri-user-settings-line" urgent={!requirement.responsableName} />
            </div>
          </div>

          {/* Compliance Semaphore */}
          <ComplianceSemaphore
            semaforo={requirement.semaforo}
            motivo={requirement.semaforoMotivo}
            fechaLimite={requirement.fechaLimite}
            responsableName={requirement.responsableName}
            insumosPendientes={inputs.filter((i) => i.estado === "pendiente" || i.estado === "en_desarrollo").length}
            insumosCompletos={inputs.filter((i) => i.estado === "entregado" || i.estado === "aprobado").length}
            insumosTotal={inputs.length}
          />

          {/* IA Extraction */}
          {extraction && <IAExtractionPanel extraction={extraction} />}

          {/* Insumos Solicitados */}
          {inputs.length > 0 && (
            <div className="bg-white border border-background-200/70 rounded-xl overflow-hidden">
              <div className="px-5 py-3.5 border-b border-background-200/50 flex items-center justify-between">
                <h3 className="text-sm font-bold text-foreground-900">Insumos Solicitados ({inputs.length})</h3>
              </div>
              <div className="divide-y divide-background-100/70">
                {inputs.map((input) => (
                  <div key={input.id} className="px-5 py-3.5 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground-800">{input.tipoInsumo}</p>
                      <p className="text-xs text-foreground-500 mt-0.5 truncate">{input.descripcion}</p>
                      <div className="flex items-center gap-3 mt-1.5 text-[11px] text-foreground-400">
                        <span className="flex items-center gap-1"><i className="ri-user-line" /> {input.responsableName}</span>
                        <span className="flex items-center gap-1"><i className="ri-time-line" /> {input.fechaLimite}</span>
                      </div>
                    </div>
                    <InputStateBadge estado={input.estado} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tasks */}
          {tasks.length > 0 && (
            <div className="bg-white border border-background-200/70 rounded-xl overflow-hidden">
              <div className="px-5 py-3.5 border-b border-background-200/50">
                <h3 className="text-sm font-bold text-foreground-900">Tareas ({tasks.length})</h3>
              </div>
              <div className="divide-y divide-background-100/70">
                {tasks.map((task) => (
                  <div key={task.id} className="px-5 py-3.5 flex items-start gap-3">
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      task.estado === "completada" ? "bg-emerald-100 text-emerald-500" :
                      task.estado === "vencida" ? "bg-red-100 text-red-500" :
                      "bg-foreground-100 text-foreground-400"
                    }`}>
                      <i className={`${task.estado === "completada" ? "ri-check-line" : task.estado === "vencida" ? "ri-close-line" : "ri-time-line"} text-xs`} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground-800">{task.titulo}</p>
                      <p className="text-xs text-foreground-500 mt-0.5">{task.descripcion}</p>
                      <div className="flex items-center gap-3 mt-1.5 text-[11px]">
                        <span className="text-foreground-400">{task.responsableName}</span>
                        <span className={`font-medium ${task.estado === "vencida" ? "text-red-600" : "text-foreground-400"}`}>Límite: {task.fechaLimite}</span>
                      </div>
                    </div>
                    <TaskStateBadge estado={task.estado} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Observaciones */}
          {requirement.observaciones && (
            <div className="bg-white border border-background-200/70 rounded-xl p-5">
              <h3 className="text-sm font-bold text-foreground-900 mb-2">Observaciones</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">{requirement.observaciones}</p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status Timeline */}
          <div className="bg-white border border-background-200/70 rounded-xl p-5">
            <h3 className="text-sm font-bold text-foreground-900 mb-4">Trazabilidad</h3>
            <div className="space-y-0">
              {logs.map((log, i) => (
                <div key={log.id} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1.5 ${
                      i === 0 ? "bg-primary-500 ring-2 ring-primary-100" : "bg-foreground-300"
                    }`} />
                    {i < logs.length - 1 && <div className="w-px flex-1 bg-background-200 my-0.5" />}
                  </div>
                  <div className="pb-4">
                    <p className="text-xs font-semibold text-foreground-800">{log.accion}</p>
                    <p className="text-[11px] text-foreground-500 mt-0.5">{log.detalle}</p>
                    <p className="text-[11px] text-foreground-400 mt-1">{log.userName} · {new Date(log.createdAt).toLocaleDateString("es-CL", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Document Info */}
          <div className="bg-white border border-background-200/70 rounded-xl p-5">
            <h3 className="text-sm font-bold text-foreground-900 mb-3">Documentos</h3>
            <div className="space-y-2">
              <div className="flex items-center gap-2.5 p-2.5 bg-background-50 rounded-lg">
                <span className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                  <i className="ri-file-pdf-line text-red-500" />
                </span>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-foreground-800 truncate">{requirement.documentoOrigenNombre || "Oficio_Judicial.pdf"}</p>
                  <p className="text-[10px] text-foreground-400">Documento original del tribunal</p>
                </div>
                <button 
                  onClick={() => handleDownloadFile(requirement.documentoOrigenNombre || "Oficio_Judicial.pdf", "Oficio Judicial Oficial: Causa RIT " + requirement.rit)}
                  className="ml-auto flex-shrink-0 w-7 h-7 rounded-lg hover:bg-background-100 flex items-center justify-center cursor-pointer"
                >
                  <i className="ri-download-line text-foreground-500 text-sm" />
                </button>
              </div>
              {requirement.comprobanteEnvioNombre && (
                <div className="flex items-center gap-2.5 p-2.5 bg-emerald-50 rounded-lg border border-emerald-200">
                  <span className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-double-line text-emerald-500" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-emerald-800 truncate">{requirement.comprobanteEnvioNombre}</p>
                    <p className="text-[10px] text-emerald-600">Comprobante de envío · {requirement.fechaEnvio}</p>
                  </div>
                  <button 
                    onClick={() => handleDownloadFile(requirement.comprobanteEnvioNombre || "comprobante.pdf", "Comprobante de envio oficial para causa: " + requirement.rit)}
                    className="ml-auto flex-shrink-0 w-7 h-7 rounded-lg hover:bg-emerald-100 flex items-center justify-center cursor-pointer"
                  >
                    <i className="ri-download-line text-emerald-600 text-sm" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoField({ label, value, icon, highlight, urgent }: { label: string; value: string; icon: string; highlight?: boolean; urgent?: boolean }) {
  return (
    <div className="flex items-start gap-2.5">
      <span className={`w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5 ${urgent ? "text-red-400" : "text-foreground-400"}`}>
        <i className={`${icon} text-sm`} />
      </span>
      <div>
        <p className="text-[11px] text-foreground-400 font-medium uppercase tracking-wider">{label}</p>
        <p className={`text-sm font-medium ${urgent ? "text-red-600" : highlight ? "text-amber-600 font-bold" : "text-foreground-800"}`}>{value}</p>
      </div>
    </div>
  );
}

function InputStateBadge({ estado }: { estado: string }) {
  const map: Record<string, string> = {
    pendiente: "bg-foreground-100 text-foreground-700 border-foreground-200",
    en_desarrollo: "bg-sky-100 text-sky-700 border-sky-200",
    entregado: "bg-emerald-100 text-emerald-700 border-emerald-200",
    devuelto: "bg-red-100 text-red-700 border-red-200",
    aprobado: "bg-emerald-100 text-emerald-700 border-emerald-200",
    vencido: "bg-red-100 text-red-700 border-red-200",
  };
  const labels: Record<string, string> = {
    pendiente: "Pendiente",
    en_desarrollo: "En desarrollo",
    entregado: "Entregado",
    devuelto: "Devuelto",
    aprobado: "Aprobado",
    vencido: "Vencido",
  };
  return (
    <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium border flex-shrink-0 ${map[estado] || "bg-foreground-100 text-foreground-700"}`}>
      {labels[estado] || estado}
    </span>
  );
}

function TaskStateBadge({ estado }: { estado: string }) {
  const map: Record<string, string> = {
    pendiente: "bg-foreground-100 text-foreground-700 border-foreground-200",
    en_progreso: "bg-sky-100 text-sky-700 border-sky-200",
    completada: "bg-emerald-100 text-emerald-700 border-emerald-200",
    vencida: "bg-red-100 text-red-700 border-red-200",
  };
  const labels: Record<string, string> = {
    pendiente: "Pendiente",
    en_progreso: "En progreso",
    completada: "Completada",
    vencida: "Vencida",
  };
  return (
    <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium border flex-shrink-0 ${map[estado] || "bg-foreground-100 text-foreground-700"}`}>
      {labels[estado] || estado}
    </span>
  );
}